import React, { useEffect, useState } from 'react';
import { errorMessage, toastError, toastSuccess } from '../../lib/toast';
import { createAccessRequest, getSpecialities, listOf } from './vendorsApi';
import { LEVEL_LABEL, VENDOR_ERRORS, levelRank } from './vendorLabels';
import { Dialog, InlineError } from './VendorBits';

const REASON_MAX = 500;
// Owners can't hand out approve (spec call 8), so it is never requestable.
const REQUEST_LEVELS = ['view', 'comment', 'propose'];

// "Pending" here means a pending access request, not a pending vendor change.
const ERRORS = { ...VENDOR_ERRORS, pending_exists: 'You already have a pending request for this speciality.' };

/** Pick a speciality (names only, never vendors), a level and a reason. */
export default function RequestAccessModal({ me, onClose, onDone }) {
  const [specialities, setSpecialities] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [tagId, setTagId] = useState('');
  const [level, setLevel] = useState('view');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    getSpecialities()
      .then((d) => { if (live) setSpecialities(listOf(d, 'specialities')); })
      .catch(() => { if (live) setLoadFailed(true); });
    return () => { live = false; };
  }, []);

  // Specialities where the caller is already at approve have nothing to request.
  const atApprove = new Set((me?.levels || []).filter((l) => levelRank(l.level) >= levelRank('approve')).map((l) => l.tag_id));
  const options = (specialities || []).filter((s) => !atApprove.has(s.id));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const found = {};
    if (!tagId) found.tag = 'Pick a speciality.';
    if (!reason.trim()) found.reason = 'Tell the owner why you need access.';
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      await createAccessRequest({ tag_id: Number(tagId), level, reason: reason.trim() });
      toastSuccess('Access requested.');
      onDone();
    } catch (err) {
      toastError(errorMessage(err, 'Could not send the request.', ERRORS));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title="Request access" onClose={onClose} labelledBy="vd-access-title">
      {loadFailed && <InlineError message="Couldn't load specialities. Try again in a minute." />}
      <form onSubmit={submit} noValidate>
        <div className="vd-form-row">
          <label htmlFor="vd-access-tag">Speciality</label>
          <select id="vd-access-tag" value={tagId} onChange={(e) => setTagId(e.target.value)} disabled={busy || !specialities}>
            <option value="">Select a speciality</option>
            {options.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {errors.tag && <p className="vd-field-error" role="alert">{errors.tag}</p>}
        </div>
        <div className="vd-form-row">
          <label htmlFor="vd-access-level">Level</label>
          <select id="vd-access-level" value={level} onChange={(e) => setLevel(e.target.value)} disabled={busy}>
            {REQUEST_LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
          </select>
        </div>
        <div className="vd-form-row">
          <label htmlFor="vd-access-reason">Reason</label>
          <textarea id="vd-access-reason" rows={3} maxLength={REASON_MAX} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
          <span className="vd-counter">{reason.length}/{REASON_MAX}</span>
          {errors.reason && <p className="vd-field-error" role="alert">{errors.reason}</p>}
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>Request access</button>
        </div>
      </form>
    </Dialog>
  );
}
